import { t } from "@airange/shared/i18n-identity-picker";
import { ModalForm, type ProColumns } from "@ant-design/pro-components";
import { Empty, Flex, Input, Tree, type TreeProps, Typography } from "antd";
import type { ReactNode } from "react";
import {
  AdminPickerWorkspace,
  type AdminPickerWorkspaceTableProps,
} from "./admin-picker-workspace";
import { usePickerRowDoubleClick, withPickerRowDoubleClick } from "./picker-row-double-click";

type PickerTreeNode = NonNullable<TreeProps["treeData"]>[number];

type OrganizationUserPickerTableProps<
  T extends Record<string, unknown>,
  U extends object,
> = AdminPickerWorkspaceTableProps<T, U>;

export type OrganizationUserPickerModalProps<
  T extends Record<string, unknown>,
  U extends object = Record<string, unknown>,
> = {
  title: ReactNode;
  submitText: string;
  cancelText: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: () => Promise<boolean>;
  columns: readonly ProColumns<T>[];
  rowKey: string | ((record: T) => string | number);
  selectedRowKeys: readonly (string | number)[];
  onSelectionChange: (keys: Array<string | number>, rows: T[]) => void;
  persistenceKey: string;
  emptyText: ReactNode;
  tableTitle?: ReactNode;
  tableExtra?: ReactNode;
  treeTitle?: ReactNode;
  treeExtra?: ReactNode;
  treeData: readonly PickerTreeNode[];
  treeLoading?: boolean;
  treeSearchValue?: string;
  onTreeSearchValueChange?: (value: string) => void;
  treeSearchPlaceholder?: string;
  selectedTreeKeys?: string[];
  onTreeSelect?: (selectedKeys: string[]) => void;
  treeDefaultExpandAll?: boolean;
  width?: number | string;
  rowSelectionType?: "radio" | "checkbox";
  getCheckboxProps?: (record: T) => { disabled?: boolean };
  tableLayout?: "auto" | "fixed";
  showToolbarOptions?: boolean;
  defaultColumnsState?: Record<string, { show: boolean }>;
  tableProps?: OrganizationUserPickerTableProps<T, U>;
};

export function OrganizationUserPickerModal<
  T extends Record<string, unknown>,
  U extends object = Record<string, unknown>,
>({
  title,
  submitText,
  cancelText,
  open,
  onOpenChange,
  onSubmit,
  columns,
  rowKey,
  selectedRowKeys,
  onSelectionChange,
  persistenceKey,
  emptyText,
  tableTitle,
  tableExtra,
  treeTitle,
  treeExtra,
  treeData,
  treeLoading = false,
  treeSearchValue,
  onTreeSearchValueChange,
  treeSearchPlaceholder,
  selectedTreeKeys = [],
  onTreeSelect,
  treeDefaultExpandAll = true,
  width = 1200,
  rowSelectionType = "checkbox",
  getCheckboxProps,
  tableLayout = "auto",
  showToolbarOptions = true,
  defaultColumnsState,
  tableProps,
}: OrganizationUserPickerModalProps<T, U>) {
  const normalizedTreeData = treeData.slice();
  const normalizedTreeTitle = treeTitle ?? t("common.identityPicker.treeTitle");
  const normalizedTreeSearchPlaceholder =
    treeSearchPlaceholder ?? t("common.identityPicker.treeSearchPlaceholder");
  const handleResetClick = () => {
    onOpenChange(false);
  };
  const rowDoubleClick = usePickerRowDoubleClick({
    open,
    rowKey,
    rowSelectionType,
    selectedRowKeys,
    onSelectionChange,
    ...(getCheckboxProps ? { getCheckboxProps } : {}),
    onSubmit,
    onClose: () => onOpenChange(false),
  });
  const tableOnRow = tableProps?.onRow;

  return (
    <ModalForm<Record<string, never>>
      title={title}
      open={open}
      onOpenChange={onOpenChange}
      modalProps={{ destroyOnHidden: true, width }}
      submitter={{
        searchConfig: { submitText, resetText: cancelText },
        resetButtonProps: { onClick: handleResetClick },
      }}
      onFinish={() => onSubmit()}
    >
      <Flex gap={16} align="stretch">
        <div style={{ width: 320, minWidth: 280 }}>
          <Flex vertical gap={12}>
            <Flex justify="space-between" align="center" gap={8}>
              <Typography.Text strong>{normalizedTreeTitle}</Typography.Text>
              {treeExtra}
            </Flex>
            {onTreeSearchValueChange ? (
              <Input.Search
                allowClear
                value={treeSearchValue}
                placeholder={normalizedTreeSearchPlaceholder}
                onChange={(event) => onTreeSearchValueChange(event.target.value)}
              />
            ) : null}
            <div
              style={{
                minHeight: 420,
                maxHeight: 560,
                overflow: "auto",
                border: "1px solid var(--ant-color-border-secondary)",
                borderRadius: 8,
                padding: 12,
                background: "var(--ant-color-bg-container)",
              }}
            >
              {normalizedTreeData.length > 0 ? (
                <Tree
                  blockNode
                  defaultExpandAll={treeDefaultExpandAll}
                  selectedKeys={selectedTreeKeys}
                  treeData={normalizedTreeData}
                  onSelect={(keys) => {
                    onTreeSelect?.(keys.map((item) => String(item)));
                  }}
                />
              ) : (
                <Flex align="center" justify="center" style={{ minHeight: 396 }}>
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      treeLoading
                        ? t("common.identityPicker.treeLoading")
                        : t("common.identityPicker.treeEmpty")
                    }
                  />
                </Flex>
              )}
            </div>
          </Flex>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Flex vertical gap={12}>
            {tableTitle || tableExtra ? (
              <Flex justify="space-between" align="center" gap={8}>
                <Typography.Text strong>{tableTitle}</Typography.Text>
                {tableExtra}
              </Flex>
            ) : null}
            <AdminPickerWorkspace<T, U>
              columns={columns}
              rowKey={rowKey}
              persistenceKey={persistenceKey}
              showToolbarOptions={showToolbarOptions}
              {...(defaultColumnsState ? { defaultColumnsState } : {})}
              emptyText={emptyText}
              tableLayout={tableLayout}
              tableProps={{
                ...(tableProps ?? {}),
                ...(rowDoubleClick.enabled
                  ? {
                      onRow: withPickerRowDoubleClick(tableOnRow, rowDoubleClick.onRowDoubleClick),
                    }
                  : {}),
              }}
              selectedRowKeys={selectedRowKeys}
              onSelectionChange={onSelectionChange}
              rowSelectionType={rowSelectionType}
              {...(getCheckboxProps ? { getCheckboxProps } : {})}
              preserveSelectedRowKeys
            />
          </Flex>
        </div>
      </Flex>
    </ModalForm>
  );
}
